import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ChevronLeft, ChevronRight, Waves, Bike, Footprints, Zap, Timer, Trophy, Target } from "lucide-react";
import {
  DISTANCE_OPTIONS, LEVEL_OPTIONS, DURATION_OPTIONS,
  INTENSITY_OPTIONS, PLAN_MATRIX
} from "@/lib/constants";

const StepIndicator = ({ current, total }) => (
  <div className="flex items-center gap-2 mb-8" data-testid="step-indicator">
    {Array.from({ length: total }, (_, i) => (
      <div key={i} className="flex items-center gap-2">
        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all duration-300 ${
          i < current ? "bg-slate-900 text-white" :
          i === current ? "bg-orange-500 text-white scale-110" :
          "bg-slate-200 text-slate-500"
        }`}>
          {i + 1}
        </div>
        {i < total - 1 && (
          <div className={`h-0.5 w-8 sm:w-12 transition-all duration-500 ${
            i < current ? "bg-slate-900" : "bg-slate-200"
          }`} />
        )}
      </div>
    ))}
  </div>
);

const OptionCard = ({ selected, onClick, children, testId }) => (
  <div
    className={`wizard-card ${selected ? "selected" : "border-slate-200"}`}
    onClick={onClick}
    data-testid={testId}
  >
    {children}
  </div>
);

export default function WizardPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [distance, setDistance] = useState(null);
  const [level, setLevel] = useState(null);
  const [duration, setDuration] = useState(null);
  const [intensity, setIntensity] = useState(null);

  const canNext = () => {
    if (step === 0) return !!distance;
    if (step === 1) return !!level;
    if (step === 2) return !!duration;
    if (step === 3) return !!intensity;
    return true;
  };

  const handleNext = () => {
    if (step < 4) setStep(step + 1);
  };
  const handleBack = () => {
    if (step > 0) setStep(step - 1);
  };

  const getPlanCode = () => {
    if (!distance || !duration || !intensity) return null;
    return PLAN_MATRIX[distance]?.[duration]?.[intensity] || null;
  };

  const handleContinue = () => {
    navigate("/profile", {
      state: { distance, level, duration, intensity, planCode: getPlanCode() }
    });
  };

  const levelIcons = { finisher: Target, intermediate: Trophy, advanced: Zap };

  return (
    <div className="min-h-screen track-texture">
      {/* Header */}
      <header className="bg-slate-900 text-white py-6 px-4 no-print">
        <div className="max-w-2xl mx-auto">
          <h1 className="text-3xl sm:text-4xl font-black uppercase tracking-tight">
            <span className="text-orange-400">BoiDapChay</span> Ironman AI
          </h1>
          <p className="text-slate-400 mt-1 text-sm font-medium">
            Tạo kế hoạch tập luyện triathlon cá nhân hoá
          </p>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-8">
        <StepIndicator current={step} total={5} />

        {/* Step 0: Distance */}
        {step === 0 && (
          <div className="animate-fade-in-up">
            <h2 className="text-2xl sm:text-3xl font-bold uppercase tracking-tight mb-2">
              Chọn cự ly thi đấu
            </h2>
            <p className="text-slate-500 mb-6 text-sm">Bạn đang chuẩn bị cho cuộc đua nào?</p>
            <div className="grid gap-4">
              {DISTANCE_OPTIONS.map((opt) => (
                <OptionCard
                  key={opt.value}
                  selected={distance === opt.value}
                  onClick={() => setDistance(opt.value)}
                  testId={`distance-${opt.value}`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-16 h-16 rounded-xl flex items-center justify-center text-2xl font-black ${
                      distance === opt.value ? "bg-orange-500 text-white" : "bg-slate-100 text-slate-700"
                    }`}>
                      {opt.value === "70.3" ? "70.3" : "140.6"}
                    </div>
                    <div>
                      <div className="font-bold text-lg">{opt.subtitle}</div>
                      <div className="text-slate-500 text-sm flex items-center gap-2 flex-wrap mt-1">
                        <span className="inline-flex items-center gap-1"><Waves className="w-3 h-3 text-sky-500" />{opt.value === "70.3" ? "1.9km" : "3.8km"}</span>
                        <span className="inline-flex items-center gap-1"><Bike className="w-3 h-3 text-emerald-500" />{opt.value === "70.3" ? "90km" : "180km"}</span>
                        <span className="inline-flex items-center gap-1"><Footprints className="w-3 h-3 text-red-500" />{opt.value === "70.3" ? "21.1km" : "42.2km"}</span>
                      </div>
                    </div>
                  </div>
                </OptionCard>
              ))}
            </div>
          </div>
        )}

        {/* Step 1: Level */}
        {step === 1 && (
          <div className="animate-fade-in-up">
            <h2 className="text-2xl sm:text-3xl font-bold uppercase tracking-tight mb-2">
              Trình độ của bạn
            </h2>
            <p className="text-slate-500 mb-6 text-sm">Chọn trình độ phù hợp với kinh nghiệm hiện tại</p>
            <div className="grid gap-4">
              {LEVEL_OPTIONS.map((opt) => {
                const Icon = levelIcons[opt.value];
                return (
                  <OptionCard
                    key={opt.value}
                    selected={level === opt.value}
                    onClick={() => setLevel(opt.value)}
                    testId={`level-${opt.value}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                        level === opt.value ? "bg-orange-500 text-white" : "bg-slate-100 text-slate-600"
                      }`}>
                        <Icon className="w-6 h-6" />
                      </div>
                      <div>
                        <div className="font-bold text-lg">{opt.label}</div>
                        <div className="text-slate-500 text-sm">{opt.desc}</div>
                      </div>
                    </div>
                  </OptionCard>
                );
              })}
            </div>
          </div>
        )}

        {/* Step 2: Duration */}
        {step === 2 && distance && (
          <div className="animate-fade-in-up">
            <h2 className="text-2xl sm:text-3xl font-bold uppercase tracking-tight mb-2">
              Độ dài plan
            </h2>
            <p className="text-slate-500 mb-6 text-sm">Bạn có bao nhiêu tuần để chuẩn bị?</p>
            <div className="grid gap-4">
              {DURATION_OPTIONS[distance].map((opt) => (
                <OptionCard
                  key={opt.value}
                  selected={duration === opt.value}
                  onClick={() => setDuration(opt.value)}
                  testId={`duration-${opt.value}`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                      duration === opt.value ? "bg-orange-500 text-white" : "bg-slate-100 text-slate-600"
                    }`}>
                      <Timer className="w-6 h-6" />
                    </div>
                    <div>
                      <div className="font-bold text-lg">{opt.label}</div>
                      <div className="text-slate-500 text-sm">{opt.desc}</div>
                    </div>
                  </div>
                </OptionCard>
              ))}
            </div>
          </div>
        )}

        {/* Step 3: Intensity */}
        {step === 3 && distance && (
          <div className="animate-fade-in-up">
            <h2 className="text-2xl sm:text-3xl font-bold uppercase tracking-tight mb-2">
              Cường độ mong muốn
            </h2>
            <p className="text-slate-500 mb-6 text-sm">Bạn muốn tập bao nhiêu giờ mỗi tuần?</p>
            <div className="grid gap-4">
              {INTENSITY_OPTIONS[distance].map((opt) => (
                <OptionCard
                  key={opt.value}
                  selected={intensity === opt.value}
                  onClick={() => setIntensity(opt.value)}
                  testId={`intensity-${opt.value}`}
                >
                  <div>
                    <div className="font-bold text-lg">{opt.label}</div>
                    <div className="text-orange-600 font-semibold text-sm mt-1">{opt.hours}</div>
                  </div>
                </OptionCard>
              ))}
            </div>
          </div>
        )}

        {/* Step 4: Recommendation */}
        {step === 4 && (
          <div className="animate-fade-in-up">
            <h2 className="text-2xl sm:text-3xl font-bold uppercase tracking-tight mb-2">
              Gợi ý plan của bạn
            </h2>
            {getPlanCode() ? (
              <div className="bg-white rounded-2xl border-2 border-slate-200 p-6 shadow-lg mt-4">
                <Badge className="bg-orange-500 text-white text-lg px-4 py-1 mb-4">
                  {getPlanCode()}
                </Badge>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between py-2 border-b border-slate-100">
                    <span className="text-slate-500">Cự ly</span>
                    <span className="font-bold">{distance === "70.3" ? "Half Ironman 70.3" : "Full Ironman 140.6"}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-slate-100">
                    <span className="text-slate-500">Trình độ</span>
                    <span className="font-bold capitalize">{level}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-slate-100">
                    <span className="text-slate-500">Độ dài</span>
                    <span className="font-bold">{duration} tuần</span>
                  </div>
                  <div className="flex justify-between py-2">
                    <span className="text-slate-500">Cường độ</span>
                    <span className="font-bold capitalize">{intensity === "light" ? "Nhẹ" : intensity === "medium" ? "Trung bình" : "Nặng"}</span>
                  </div>
                </div>

                {/* Warning for mismatched level */}
                {(() => {
                  const code = getPlanCode();
                  if (!code) return null;
                  const planLevel = code.startsWith("F") ? "finisher" : code.startsWith("I") ? "intermediate" : "advanced";
                  if (planLevel !== level) {
                    return (
                      <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm" data-testid="level-mismatch-warning">
                        Lưu ý: Cường độ này thường phù hợp với trình độ <strong className="capitalize">{planLevel}</strong>.
                        Bạn đã chọn trình độ <strong className="capitalize">{level}</strong>. Hãy đảm bảo bạn sẵn sàng cho cường độ này.
                      </div>
                    );
                  }
                  return null;
                })()}

                <Button
                  className="btn-skew bg-orange-500 hover:bg-orange-600 text-white w-full mt-6"
                  onClick={handleContinue}
                  data-testid="continue-to-profile-btn"
                >
                  <span>Tiếp tục cá nhân hoá</span>
                </Button>
              </div>
            ) : (
              <div className="bg-white rounded-2xl border-2 border-red-200 p-6 mt-4" data-testid="no-plan-warning">
                <div className="text-red-600 font-bold mb-2">Plan này không có sẵn</div>
                <p className="text-slate-600 text-sm">
                  Kết hợp cự ly {distance}, cường độ {intensity === "light" ? "Nhẹ" : intensity === "medium" ? "Trung bình" : "Nặng"} và {duration} tuần không có plan tương ứng.
                  Hãy chọn cường độ cao hơn hoặc thời gian dài hơn.
                </p>
                <Button variant="outline" className="mt-4" onClick={() => setStep(3)} data-testid="change-intensity-btn">
                  <span>Đổi cường độ</span>
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Navigation */}
        {step < 4 && (
          <div className="flex justify-between mt-8">
            <Button
              variant="ghost"
              onClick={handleBack}
              disabled={step === 0}
              className="text-slate-600"
              data-testid="wizard-back-btn"
            >
              <ChevronLeft className="w-4 h-4 mr-1" /> Quay lại
            </Button>
            <Button
              className="btn-skew bg-slate-900 hover:bg-slate-800 text-white"
              onClick={handleNext}
              disabled={!canNext()}
              data-testid="wizard-next-btn"
            >
              <span>Tiếp theo <ChevronRight className="w-4 h-4 ml-1 inline" /></span>
            </Button>
          </div>
        )}

        {step === 4 && (
          <Button
            variant="ghost"
            onClick={handleBack}
            className="mt-4 text-slate-600"
            data-testid="wizard-back-btn-final"
          >
            <ChevronLeft className="w-4 h-4 mr-1" /> Quay lại chỉnh sửa
          </Button>
        )}
      </main>
    </div>
  );
}
